import { genericPropertyDetailsKeys } from "../../@types/sharedSchema";

/**
 * THE Hebrew label for each amenity boolean — one definition, for every surface.
 *
 * ── Why this file exists ─────────────────────────────────────────────────────────
 *
 * There were two byte-identical copies of this map (`matchesDisplay.ts` and
 * `OverviewSearchProfilePanel.tsx`), each with a comment explaining that it was inline
 * "by CRM convention". That convention is for a label used ONCE; these thirteen strings
 * are rendered by the search-profile chips, the match filter chips, the listing quick
 * view, the bulk editor and now an inline editor that has to un-tick exactly what the
 * chips displayed. A realtor reading "ממ״ד" on a chip and "מרחב מוגן" in the editor that
 * changes it would reasonably conclude they are two different fields.
 *
 * ⚠️ THE KEY LIST IS `genericPropertyDetailsKeys`, AND IT IS THE WHOLE LIST — all 13.
 *
 * Do NOT build an amenity UI on `VISIBLE_BOOLEAN_FILTER_KEYS` (the listings-match filter's
 * set), which deliberately hides four of them. That set answers "which amenities are worth
 * FILTERING listings by"; this one answers "which amenities can a contact require". An
 * editor built on the shorter list would render a contact's stored `hasGarden: true`
 * nowhere — visible on the chip beside it, and impossible to remove.
 *
 * {@link amenityLabel} falls back to the raw key rather than throwing or hiding the row:
 * a new member of the enum should show up as an ugly `hasSomething` that somebody fixes,
 * never as an amenity that silently cannot be edited.
 */
export const AMENITY_LABELS: Readonly<Record<string, string>> = {
  hasBars: "סורגים",
  hasGarden: "גינה",
  hasParking: "חניה",
  hasStorage: "מחסן",
  hasTerrace: "מרפסת",
  isRenovated: "משופץ",
  hasElevator: "מעלית",
  hasProtectedSpace: "ממ״ד",
  hasAirConditioning: "מיזוג",
  hasHandicappedAccess: "גישה לנכים",
  hasHousingUnit: "יחידת דיור",
  hasMamak: "ממ״ק",
  hasMiklat: "מקלט"
};

export const amenityLabel = (key: string): string => AMENITY_LABELS[key] ?? key;

/**
 * Every amenity key, in the canonical order, paired with its label.
 *
 * The order is `genericPropertyDetailsKeys`' own — the same order the schema declares and
 * the chips render — rather than `Object.keys` on the map above, whose order is an
 * implementation detail of an object literal.
 */
export const AMENITY_OPTIONS: ReadonlyArray<{ key: string; label: string }> =
  genericPropertyDetailsKeys.map((key) => ({ key, label: amenityLabel(key) }));

/**
 * ממ״ד / ממ״ק / מקלט — the one amenity group whose members are ALTERNATIVES rather than separate
 * requirements. A buyer who wants shelter takes whichever the building has.
 *
 * Reported 2026-09-22: a realtor ticking all three got nothing anywhere. Every surface ANDed them
 * — the listing→buyers rules excluded on a `hasProtectedSpace: false`, and the contact→listings
 * search asked Postgres for `amenities @> ARRAY[all three]`, which no listing in the mirror
 * satisfies, so the התאמת נכס page came back empty with no explanation. Ticking MORE of what you
 * would accept must never narrow the answer.
 *
 * The server's matching half is `PROTECTED_SPACE_GROUP` in
 * `server/api/services/listings/listingMatchRules.ts` and the `amenities && …` leg in
 * `listingsSearch.service.ts`. All three move together, or one surface silently keeps the AND.
 *
 * Order is the display order, and `[0]` is the group's representative key on the server.
 */
export const PROTECTED_SPACE_KEYS: ReadonlyArray<string> = ["hasProtectedSpace", "hasMamak", "hasMiklat"];

export const PROTECTED_SPACE_GROUP_LABEL = "מרחב מוגן";

/** Said under the group once anything in it is ticked — the whole point of grouping them. */
export const PROTECTED_SPACE_GROUP_HINT =
  "אפשר לסמן כמה אפשרויות — יוצגו נכסים שיש בהם לפחות אחת מהן";

/** The amenity keys that are NOT part of a group, in their canonical order. */
export const STANDALONE_AMENITY_OPTIONS: ReadonlyArray<{ key: string; label: string }> = AMENITY_OPTIONS.filter(
  (option) => !PROTECTED_SPACE_KEYS.includes(option.key)
);

export const PROTECTED_SPACE_OPTIONS: ReadonlyArray<{ key: string; label: string }> = PROTECTED_SPACE_KEYS.map((key) => ({
  key,
  label: amenityLabel(key)
}));

/** True once the contact (or the filter) has ticked anything in the מרחב מוגן group. */
export const hasAnyProtectedSpace = (values: Record<string, boolean | undefined>): boolean =>
  PROTECTED_SPACE_KEYS.some((key) => values[key] === true);

/**
 * The amenities a bag ASKS FOR: every key that holds a real `true`, and nothing else.
 *
 * ⚠️ `=== true`, NEVER truthiness — and never the stored value copied as it is.
 *
 * An amenity field on a contact is not always a boolean. Until September 2026 the file import
 * wrote a cell as the spreadsheet printed it, and Webot's own export came back through it with
 * its yes/no columns as the WORDS `"TRUE"` and `"FALSE"`: measured 2026-10-14, 1,556 contacts
 * of 31 accounts hold that text (`"TRUE"` in 3,496 fields, `"FALSE"` in 299, no other text; the
 * newest was written on 2026-08-25, before the import got its whitelist of columns).
 *
 * Every reader of a contact's wishes already reads them strictly: the card's chips, the cell
 * editor (`readAmenities`), the contact page's match filters, the server's matching rules. The
 * listings search did not. Picking a contact copied the fields as stored, the text is truthy, so
 * the chip lit up and the request carried `booleanFilters.hasProtectedSpace: "TRUE"`. The
 * server refuses anything but a boolean there (400), and the realtor saw an empty result: 15
 * refused searches from one account in a week. `"FALSE"` is exactly as truthy.
 *
 * So a bag is read through here where a search takes it IN (a picked contact, a restored
 * address) and where it sends it OUT (both request builders): an amenity is asked for, or it
 * is not sent.
 *
 * `keys` narrows the answer to those keys, in their order; without it every key of the bag
 * is considered.
 */
export const tickedAmenities = (
  source: Readonly<Record<string, unknown>> | null | undefined,
  keys?: ReadonlyArray<string>
): Record<string, true> => {
  const ticked: Record<string, true> = {};
  if (!source || typeof source !== "object") return ticked;
  (keys ?? Object.keys(source)).forEach((key) => {
    if ((source as Record<string, unknown>)[key] === true) ticked[key] = true;
  });
  return ticked;
};
